import React from 'react';
import { DotsVerticalIcon } from '@radix-ui/react-icons';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateUtils';

export interface Document {
  id: string;
  name: string;
  type: string;
  upload_date: string;
  size: string;
  status: string;
  error_message?: string;
  s3_url?: string;
  presigned_url?: string;
  page_count?: number;
  is_archived?: boolean;
}

interface DocumentsTableProps {
  documents: Document[];
  selectedDocuments: string[];
  onSelectDocument: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onDocumentClick?: (id: string, type: string, status: string) => void;
  onRetryDocument?: (id: string) => void;
  onDeleteDocument?: (id: string) => void;
  onToggleArchive?: (id: string) => void;
  isDeletingId?: string | null;
  isArchivingId?: string | null;
  isArchived?: boolean;
}

const DocumentsTable: React.FC<DocumentsTableProps> = ({ 
  documents, 
  selectedDocuments, 
  onSelectDocument, 
  onSelectAll,
  onDocumentClick,
  onRetryDocument,
  onDeleteDocument,
  onToggleArchive,
  isDeletingId,
  isArchivingId,
  isArchived
}) => {
  const navigate = useNavigate();
  
  const handleViewDocument = (id: string, type: string, status: string) => {
    if (onDocumentClick) {
      onDocumentClick(id, type, status);
    } else {
      navigate(`/invoice/${id}`);
    }
  };
  
  return (
    <div className="bg-white dark:bg-dark-sidebar rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary dark:bg-gray-700"
                  checked={documents.length > 0 && selectedDocuments.length === documents.length}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                NAME
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                TYPE
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                UPLOAD DATE
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                PAGES
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                STATUS
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-dark-sidebar divide-y divide-gray-200 dark:divide-dark-border">
          {documents.map((document) => (
            <tr key={document.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
              <td className="px-3 py-4 whitespace-nowrap">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-primary border-gray-300 dark:border-gray-600 rounded focus:ring-primary dark:bg-gray-700"
                  checked={selectedDocuments.includes(document.id)}
                  onChange={(e) => onSelectDocument(document.id, e.target.checked)}
                />
              </td>
              <td className={`px-6 py-4 whitespace-nowrap ${document.status === 'Processed' ? 'cursor-pointer' : 'cursor-not-allowed'}`} onClick={() => handleViewDocument(document.id, document.type, document.status)}>
                <div className={`text-sm font-medium ${document.status === 'Processed' ? 'text-gray-900 dark:text-gray-100 hover:text-primary dark:hover:text-primary' : 'text-gray-400 dark:text-gray-500'}`}>{document.name}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500 dark:text-gray-400">{document.type}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500 dark:text-gray-400">{formatDate(document.upload_date)}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-500 dark:text-gray-400">{document.page_count || 'N/A'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    document.status === 'Processed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                    document.status === 'Processing' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                  }`}>
                    {document.status}
                  </span>
                  {document.status === 'Failed' && onRetryDocument && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetryDocument(document.id);
                      }}
                      className="text-xs text-primary hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                      Retry
                    </button>
                  )}
                </div>
                {document.status === 'Failed' && document.error_message && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {document.error_message}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button className="inline-flex items-center justify-center p-1 rounded-md text-gray-400 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 focus:ring-primary">
                      <DotsVerticalIcon className="h-5 w-5" />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content 
                      className="min-w-[180px] bg-white dark:bg-gray-800 rounded-md shadow-lg p-1 z-50"
                      sideOffset={5}
                      align="end"
                    >
                      <DropdownMenu.Item 
                        className={`text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-3 py-2 ${document.status === 'Processed' ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                        onClick={() => handleViewDocument(document.id, document.type, document.status)}
                      >
                        View Details
                      </DropdownMenu.Item>
                      <DropdownMenu.Item 
                        className="text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-3 py-2 cursor-pointer"
                        onClick={() => navigate('/invoices')}
                      >
                        View All Invoices
                      </DropdownMenu.Item>
                      <DropdownMenu.Item className="text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-3 py-2 cursor-pointer">
                        Download
                      </DropdownMenu.Item>
                      <DropdownMenu.Item 
                        className={`text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-3 py-2 ${
                          isArchivingId === document.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onToggleArchive && isArchivingId !== document.id) {
                            onToggleArchive(document.id);
                          }
                        }}
                      >
                        <div className="flex items-center">
                          {isArchivingId === document.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 dark:border-gray-400 mr-2"></div>
                              {isArchived ? 'Unarchiving...' : 'Archiving...'}
                            </>
                          ) : (
                            isArchived ? 'Unarchive' : 'Archive'
                          )}
                        </div>
                      </DropdownMenu.Item>
                      <DropdownMenu.Separator className="h-px bg-gray-200 dark:bg-gray-600 my-1" />
                      <DropdownMenu.Item 
                        className={`text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-3 py-2 ${
                          isDeletingId === document.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onDeleteDocument && isDeletingId !== document.id) {
                            onDeleteDocument(document.id);
                          }
                        }}
                      >
                        <div className="flex items-center">
                          {isDeletingId === document.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 dark:border-red-400 mr-2"></div>
                              Deleting...
                            </>
                          ) : (
                            'Delete'
                          )}
                        </div>
                      </DropdownMenu.Item>
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
};

export default DocumentsTable;